/**
 * API Types
 * Shared types for API requests and responses
 */

import type {
  RatingValue,
  RecommendationAttributionReceipt,
  MealType,
  CandidateMode,
  CandidateReason,
  CandidateImageCredit,
  RecipeImageCredit,
  MealPlanCandidateSummary,
  MealPlanPreview,
  CandidateMenuErrorDetails,
  PantryIntakeMode,
  PantryLocation,
  PantryIntakeItem,
  GroceryBucket,
  GroceryPurchasePriceView,
  GroceryProductView,
  MealSlotView,
  RecipeMealSlot as SharedRecipeMealSlot,
  LeftoversMealSlot as SharedLeftoversMealSlot,
  RecipeIngredientAmountKind,
} from "@batch-and-bowl/shared";

export type {
  MealType,
  CandidateMode,
  CandidateReason,
  CandidateImageCredit,
  RecipeImageCredit,
  MealPlanCandidateSummary,
  MealPlanPreview,
  CandidateMenuErrorDetails,
  PantryIntakeMode,
  PantryLocation,
  PantryIntakeItem,
};

export interface Recipe {
  id: string;
  /** Request-scoped recommendation facts; absent on ordinary recipe reads. */
  practicalFit?: import('@batch-and-bowl/shared').RecipePracticalFit;
  practicalFitRevision?: string;
  proteinChoices?: { id: string; label: string }[];
  baseProteinLabel?: string;
  proteinChoiceId?: string | null;
  proteinChoiceLabel?: string | null;
  title: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  imageCredit?: CandidateImageCredit | null;
  sourceUrl: string | null;
  // Attribution for curated/imported recipes. Optional (rather than
  // required-and-nullable like sourceUrl) so existing Recipe fixtures don't
  // need updating; "Adapted from {sourceName}" renders wherever it's set.
  sourceName?: string | null;
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
  totalTimeMinutes?: number | null;
  difficulty: "easy" | "medium" | "hard";
  cuisine: string | null;
  mealTypes: MealType[];
  tags: string[];
  servings: number;
  costEstimate: "budget" | "moderate" | "expensive";
  /** USD, per serving. Null when not yet computed for this recipe. */
  estimatedCostPerServing?: number | null;
  status: "draft" | "published";
  isPublic: boolean;
  authorId: string | null;
  instructions: string[];
  ratingAverage: number;
  ratingCount: number;
  createdAt: string;
  updatedAt: string;
  ingredients?: RecipeIngredient[];
  nutrition?: RecipeNutrition | null;
  isFavorited?: boolean;
  /**
   * Detail responses only: the server's edit decision for this viewer
   * (author of their own recipe, or admin/moderator on a catalogue recipe).
   */
  canEdit?: boolean;
  // Present only on GET /recipes/favorites?scope=household responses: who
  // in the household favorited this recipe. Absent for scope=mine (the
  // default) and everywhere else -- see apps/api/src/routes/recipes.ts.
  favoritedBy?: { userId: string; name: string };
  // The caller's own thumbs rating on this recipe, batch-attached wherever
  // the recipe appears in a meal-plan payload (GET /meal-plans,
  // GET /meal-plans/:id) -- see apps/api/src/routes/meal-plans.ts. `null`
  // when the caller hasn't rated it; absent (not just null) when the
  // endpoint doesn't enrich ratings at all (e.g. plain recipe list/detail).
  userRating?: RatingValue | null;
}

export interface RecipeIngredient {
  id: string;
  recipeId: string;
  ingredientId: string;
  quantity: number | null;
  amountKind?: RecipeIngredientAmountKind;
  unit: string;
  presentation: IngredientPresentation;
  preparation: string | null;
  notes: string | null;
  orderIndex: number;
  ingredient: Ingredient;
}

export type IngredientPresentation =
  | { mode: "measured"; quantity: number; unit: string }
  | { mode: "to_taste" }
  | { mode: "as_needed" }
  | { mode: "unspecified" };

export interface Ingredient {
  id: string;
  name: string;
  normalizedName: string;
  category: IngredientCategory;
  commonUnits: string[];
  shelfLifeDays: number | null;
  isPantryStaple: boolean;
  averagePricePerUnit: number | null;
}

export interface RecipeNutrition {
  id: string;
  recipeId: string;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  fiber: number | null;
  sodium: number | null;
  sugar?: number | null;
  saturatedFat?: number | null;
}

export interface MealPlan {
  id: string;
  userId: string;
  name: string | null;
  startDate: string;
  endDate: string;
  status: "draft" | "active" | "completed";
  // E7 safety visibility: how many otherwise-servable recipes the
  // household's allergies / dietary restrictions removed from the catalog
  // at generation time (counted separately; a recipe excluded by both
  // counts in both). 0 means "constrained dimension, nothing excluded" or
  // "no constraint"; null (or absent, for fixtures) means the plan predates
  // this feature -- the UI shows nothing in that case.
  excludedByAllergiesCount?: number | null;
  excludedByDietCount?: number | null;
  createdAt: string;
  updatedAt: string;
  mealSlots: MealSlot[];
  // NOT the full GroceryList -- GET /meal-plans
  // (apps/api/src/routes/meal-plans.ts) embeds a lightweight `select: { id,
  // items: { select: { id, checked, checkoffEpoch, checkoffOperationId } } }`
  // projection, not the real
  // GroceryList shape. Fetch GET /grocery-lists/:mealPlanId (GroceryList,
  // below) for quantities/ingredients/cost. See MealPlanGroceryList.
  groceryList?: MealPlanGroceryList;
  // Plan-owner attribution (GET /meal-plans, GET /meal-plans/:id only --
  // see apps/api/src/routes/meal-plans.ts). Optional so fixtures/other
  // endpoints that don't enrich it still satisfy the type.
  user?: { id: string; name: string };
}

/**
 * Track C/D (design spec §4, issue #433): every meal-plan response now
 * emits slots as this kind union -- `recipe`/`leftovers` carry a recipe
 * (and batch-prep coverage: `cookedBatch` on the cooking slot, `batch` on
 * a leftovers slot); `packaged_item`/`eating_out` carry an `entry` instead.
 * The old flat `cookedBySlotId`/`batchCount` fields are gone from the wire
 * -- see apps/api/src/services/meal-slot-view.ts (the only serializer) for
 * the authoritative shape. `Recipe` is bound here so every web consumer
 * gets the web `Recipe` type on recipe-carrying slots.
 */
export type {
  BatchSummary,
  MealSlotKind,
  PackagedItemMealSlot,
  EatingOutMealSlot,
} from "@batch-and-bowl/shared";
export type RecipeMealSlot = SharedRecipeMealSlot<Recipe>;
export type LeftoversMealSlot = SharedLeftoversMealSlot<Recipe>;
export type MealSlot = MealSlotView<Recipe>;

/**
 * Narrows to the two kinds that carry a recipe. Not exported from
 * @batch-and-bowl/shared (types-only package) -- defined here and mirrored
 * in apps/api/src/services/meal-slot-view.ts.
 */
export function hasRecipe(
  slot: MealSlot,
): slot is RecipeMealSlot | LeftoversMealSlot {
  return slot.kind === "recipe" || slot.kind === "leftovers";
}

/** Request-scoped single-slot suggestion. Cuisine is an exact filter only. */
export interface SuggestMealRequest {
  date: string;
  mealType: MealType;
  cuisine?: string;
}

export type SuggestMealResponse =
  | { outcome: "added"; slot: MealSlot }
  | {
      outcome: "no_match";
      reason: "NO_ELIGIBLE_RECIPE" | "NO_EXACT_CUISINE_MATCH";
    };

export interface PreviewSuggestedMealRequest extends SuggestMealRequest {
  skippedRecipeIds: string[];
}
export interface SuggestedMealPreview {
  outcome: "preview";
  algorithmVersion: string;
  recipe: Pick<Recipe, "id" | "title" | "prepTimeMinutes" | "cookTimeMinutes" | "difficulty" | "sourceUrl" | "practicalFit" | "practicalFitRevision"> & { recommendationVersion?: string };
  servings: number;
  recommendationReasons: string[];
  attribution?: RecommendationAttributionReceipt;
  recommendation?: { algorithmVersion: string; eligibleCount: number; unseenCount: number; freshCount: number; exhausted: boolean };
}
export type PreviewSuggestedMealResponse = SuggestedMealPreview | {
  outcome: "no_match";
  algorithmVersion: string;
  reason: "NO_ELIGIBLE_RECIPE" | "NO_EXACT_CUISINE_MATCH";
  attribution?: RecommendationAttributionReceipt;
  recommendation?: SuggestedMealPreview["recommendation"];
};

export interface GroceryList {
  id: string;
  mealPlanId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  items: GroceryItem[];
  // Additive authoritative database-clock sample for causal recovery. It is
  // optional during rollout because older responses do not include it.
  serverTimeMs?: number;
  // Additive, read-time cost enrichment (see enrichWithCost in
  // apps/api/src/services/grocery-list-generator.ts). Optional because it
  // is only attached by endpoints that call enrichWithCost — never
  // persisted, so not every GroceryList-shaped response will have it.
  /**
   * @deprecated Compatibility valuation for the legacy grocery UI. Checkout
   * and planned purchase consumers must use `costTotals` and its purchase
   * price projection instead.
   */
  estimatedTotalCost?: number;
  // `itemsWithCost`/`totalItems` keep their original pre-D1 meaning (any
  // price, live or curated / how many items exist) so a caller with no
  // captured live prices sees the exact values it always has. The
  // `itemsWith*Price` breakdown (Track D, D1, issue #382) is additive.
  costCoverage?: {
    itemsWithCost: number;
    totalItems: number;
    itemsWithLivePrice?: number;
    itemsWithCuratedPrice?: number;
    itemsWithoutPrice?: number;
  };
  // GET /grocery-lists/:mealPlanId only (Track D, D1, issue #382):
  // estimatedTotalCost divided by the plan's actual summed MealSlot
  // servings. Null when there are no planned servings to divide by; absent
  // on endpoints that don't compute it.
  /**
   * @deprecated Compatibility valuation derived from the legacy estimated
   * total. Use `costTotals.perServingTypical` for planned purchase costs.
   */
  estimatedCostPerServing?: number | null;
  // GET /grocery-lists/:mealPlanId only (issue #392, DECISION-013): the two
  // labelled totals. `thisShop` = whole packages for everything not in the
  // pantry (staples included); `typicalWeek` amortizes packages whose surplus
  // carries over, and pays for spoil-bound packages; `perServingTypical` derives from
  // typicalWeek, never thisShop. Every figure is number | null — null is
  // "unknown" and renders as absence with a caveat, never $0.00. Coverage
  // is per costing mode so partial pack-size coverage stays visible.
  costTotals?: ShopTotals;
  // GET /grocery-lists/:mealPlanId?groupBy=recipe only (Track D, D2, issue
  // #383): server-computed by-recipe groups. Absent on the default
  // (aisle) grouping and every other endpoint.
  recipeGroups?: GroceryRecipeGroup[];
}

export interface CostCoverageByMode {
  itemsAtLivePrice: number;
  itemsAtPaidPrice?: number;
  itemsAtPackageCost: number;
  itemsAtFractionCost: number;
  itemsNotNeeded?: number;
  itemsUnknown: number;
  totalItems: number;
}

export interface CostTotal {
  total: number | null;
  coverage: CostCoverageByMode;
}

export interface ShopTotals {
  purchasePrices?: Record<string, GroceryPurchasePriceView>;
  thisShop: CostTotal;
  typicalWeek: CostTotal;
  perServingTypical: number | null;
}

/** Quantitative pantry evidence attached to a grocery row at read time. */
export interface GroceryPantryAllocation {
  totalNeedQuantity: number | null;
  planDerivedNeedQuantity: number | null;
  pantryAllocatedQuantity: number | null;
  remainingPurchaseQuantity: number | null;
  totalNeedGrams: number | null;
  planDerivedNeedGrams: number | null;
  pantryAllocatedGrams: number | null;
  remainingPurchaseGrams: number | null;
  coverage:
    | "none"
    | "partial"
    | "full"
    | "not-needed"
    | "unknown"
    | "removed"
    | "explicit-purchase";
  reasons: string[];
  allocations: Array<{ lotId: string; allocatedGrams: number }>;
  exclusions: Array<{ lotId: string | null; reason: string }>;
}

export interface GroceryItem {
  id: string;
  groceryListId: string;
  ingredientId: string;
  quantity: number;
  hasUnquantifiedDemand?: boolean;
  unit: string;
  checked: boolean;
  // Present on every current API response. Optional only while fixtures and
  // cached pre-causality data remain supported during the rollout.
  checkoffEpoch?: string;
  checkoffOperationId?: string | null;
  notes: string | null;
  sourceRecipeIds: string[];
  // Portion of `quantity` a user added by hand (issue #374 fix round, F1)
  // via POST /grocery-lists/:mealPlanId/items, as opposed to what was
  // derived from the meal plan's recipes. Null (or absent, for existing
  // fixtures/tests written before this field existed) on a purely
  // plan-derived row. See GroceryItem.manualQuantity in schema.prisma for
  // the durability semantics this supports across a plan-edit refresh.
  manualQuantity?: number | null;
  // Grocery-line editing (spec:
  // docs/superpowers/specs/2026-08-26-grocery-list-editing-design.md).
  //
  // `removed` is the user saying "I already own this / do not buy it". The
  // read path still RETURNS removed rows -- the server does not filter them
  // -- so the UI owns the presentation: they drop out of the main list and
  // every count, and surface only in the collapsed "Removed" section with a
  // per-line Restore. Never silently drop a row just because this is true.
  //
  // `quantityOverride` is the user saying "buy this much" and, when set, is
  // what the UI displays. It does NOT rewrite `quantity`: the documented
  // invariant `quantity = derivedQuantity + (manualQuantity ?? 0)` still
  // holds, which is exactly what lets the UI show the user their own edit
  // next to what we derived.
  //
  // Both are optional here (not just nullable) so fixtures and tests written
  // before these fields existed keep compiling -- same reason as
  // `manualQuantity` above. Absent means "not removed" / "no override".
  removed?: boolean;
  quantityOverride?: number | null;
  // The user's unit for a line whose stored `unit` is "" (grocery line
  // control, #1023). Display and edit use `unitOverride ?? unit` (see
  // `displayedUnit` in GroceryList.tsx / `effectiveUnit` server-side). Only
  // ever non-null on a row whose stored `unit` is empty. Optional for the
  // same reason as `quantityOverride` above -- fixtures/tests predating
  // this field keep compiling.
  unitOverride?: string | null;
  // Read-time pantry projection's current-trip amount, when available.
  purchaseQuantity?: number | null;
  /** Current-store candidate description; quotes remain in costTotals. */
  product?: GroceryProductView | null;
  ingredient: Ingredient;
  // Additive, read-time per-item cost estimate; null when it can't be
  // computed (missing price data, unresolvable unit, etc). Optional for
  // the same reason as GroceryList's cost fields above.
  /**
   * @deprecated Compatibility valuation for legacy rows. Use the matching
   * `costTotals.purchasePrices` entry for checkout or planned purchase costs.
   */
  estimatedCost?: number | null;
  // Where `estimatedCost` came from (Track D, D1, issue #382): a captured
  // live retailer price always wins over the curated per-item fallback.
  // Optional/absent on endpoints that don't call enrichWithCost.
  priceSource?: "live" | "curated" | "none";
  // Live retailer match capture (Track D, D1, issue #382) -- persisted only
  // via the MatchReview confirm path (POST /retailers/kroger/handoff), after
  // a verified-accepted match. REVISED null invariant (fix round, BLOCKING
  // 2): `retailerProductId`/`retailerUpc` may be set alone (an
  // identity-only capture, e.g. a re-confirm with no price on the wire);
  // `retailerPrice`/`retailerPriceCapturedAt`/`retailerCapturedQuantity`
  // are still null-or-set together as a trio, never partial. See
  // GroceryItem.retailerProductId in schema.prisma for the full write-side
  // rationale. Never treat `retailerPriceCapturedAt` alone as "there is a
  // live price" -- check `retailerPrice != null` (see hasStaleLivePrice in
  // GroceryList.tsx).
  retailerProductId?: string | null;
  retailerUpc?: string | null;
  retailerPrice?: number | null;
  retailerAisle?: string | null;
  retailerPriceCapturedAt?: string | null;
  // BLOCKING 1 (fix round, issue #382): the grocery line's `quantity` at
  // the moment `retailerPrice` was captured. Not rendered directly -- only
  // used server-side (foldRetailerMatchContributions) to detect a stale
  // total across a plan-edit refresh. Included here for type completeness
  // with what the API actually returns.
  retailerCapturedQuantity?: number | null;
  // Additive, read-time pantry flag (see markPantryBuckets in
  // apps/api/src/services/grocery-list-generator.ts). Optional for the same
  // reason as the cost fields above.
  inPantry?: boolean;
  // Track D, D2 (issue #383, part c): persisted "Add back to the list"
  // override. Forces the item into the main list regardless of `inPantry`
  // -- it is read by the shared groceryBucket() predicate (see
  // @batch-and-bowl/shared) BEFORE the pantry/staple checks, same "fact
  // beats guess" spirit one level up. Optional for the same reason as
  // manualQuantity above (existing fixtures predating this field).
  pinned?: boolean;
  // Server-computed zone (see markPantryBuckets in
  // apps/api/src/services/grocery-list-generator.ts). Optional for the same
  // reason as the cost fields: only endpoints that enrich attach it. When
  // absent, derive it with groceryBucket() from @batch-and-bowl/shared --
  // the same function the server used -- never with a literal default.
  bucket?: GroceryBucket;
  // #388: server says this line is an offerable "you might already have"
  // guess. Optional for the same reason as `bucket` — only the enriching
  // endpoint attaches it. When absent, treat it as false; never derive it
  // client-side, because the gate needs catalogue fields the list payload
  // does not carry.
  stapleGuess?: boolean;
  pantryAllocation?: GroceryPantryAllocation;
}

/**
 * Track D, D2 (issue #383, part a): one server-computed by-recipe group on
 * GET /grocery-lists/:mealPlanId?groupBy=recipe (see groupItemsByRecipe in
 * apps/api/src/services/grocery-list-generator.ts). `key` is a recipe id,
 * `'multiple'` for the combined multi-recipe bucket, or `'unassigned'` for
 * the "Added by you" honest bucket.
 *
 * Fix round (issue #383 adversarial review, finding 1 -- STRUCTURAL):
 * `itemIds` references into the SINGLE flat `GroceryList.items` array, never
 * a duplicated copy of the items themselves. The original shape embedded
 * full `GroceryItem[]` here, and GroceryList.tsx rendered the by-recipe view
 * straight from that copy -- so useToggleGroceryItem's optimistic cache
 * patch to the flat `items` array left the recipe view showing stale
 * checked state until the next refetch (usePinGroceryItem only invalidates
 * -- a pin has no optimistic patch). Resolving group membership by id
 * against the one array
 * that actually gets patched makes that divergence impossible by
 * construction. See GroceryList.tsx's `itemsById` lookup for the read side.
 *
 * Also fix round finding 2: this used to carry a group-level `recipeNames`
 * union that was both buggy (merged distinct multi-recipe items' recipe
 * names into one set) and never rendered. Per-item "Used in: ..." lines
 * (GroceryList.tsx's `getRecipeNames`, resolved from each item's own
 * `sourceRecipeIds`) already cover the truthful per-item answer in every
 * grouping, so no replacement field was added here.
 */
export interface GroceryRecipeGroup {
  key: string;
  label: string;
  itemIds: string[];
}

/**
 * Track D, D3 (issue #384): one "Trim the bill" swap suggestion from
 * GET /grocery-lists/:id/savings-suggestions (see SavingsSuggestion in
 * apps/api/src/services/savings-suggestions.ts). `rationale` is factual and
 * numbers-only (no "sale"/"% off" wording -- see that module's doc); render
 * it as-is rather than reformatting.
 */
export interface SavingsSuggestion {
  fromRecipe: { id: string; title: string; slug: string };
  toRecipe: { id: string; title: string; slug: string };
  /** Every current meal-slot id cooking `fromRecipe` for the flagged
   * ingredient -- "Make the swap" acts on the first of these. */
  slotIds: string[];
  /** Dollars saved, rounded to cents. */
  delta: number;
  rationale: string;
}

export interface DismissSavingsSuggestionRequest {
  fromRecipeId: string;
  toRecipeId: string;
}

/**
 * What GET /meal-plans and GET /meal-plans/:id actually project for each
 * grocery item -- see the grocery-list `select` in
 * apps/api/src/routes/meal-plans.ts. Deliberately NOT `GroceryItem`
 * (that promises quantity/unit/ingredient/etc, which this endpoint never
 * sends -- issue #300).
 */
export type MealPlanGroceryItem = Pick<
  GroceryItem,
  "id" | "checked" | "checkoffEpoch" | "checkoffOperationId"
>;

/**
 * What PUT /grocery-lists/:id/check-item actually returns --
 * `prisma.groceryItem.update({ where, data })` with no include/select (see
 * apps/api/src/routes/grocery-lists.ts), so every scalar column comes back
 * but the `ingredient` relation does not. Found during the issue #300 audit:
 * same class of bug (a required field the client type promised but the
 * endpoint never sent), just not yet exercised by anything reading the
 * mutation's result beyond `.checked`.
 */
export type GroceryItemToggleResult = Omit<GroceryItem, "ingredient">;

export type CheckoffCausalResult =
  | { outcome: "applied" | "idempotent"; acceptedEpoch: string; acceptedOperationId: string; serverTimeMs: number }
  | { outcome: "stale"; acceptedEpoch: string; acceptedOperationId: string | null; serverTimeMs: number }
  | { outcome: "legacy-applied"; acceptedEpoch: "0"; acceptedOperationId: null; serverTimeMs: number };

export type ClearCheckedRowOutcome =
  | "applied"
  | "retained-newer"
  | "idempotent"
  | "legacy-applied";

export interface ClearCheckedCausalRow {
  itemId: string;
  checked: boolean;
  acceptedEpoch: string;
  acceptedOperationId: string | null;
  outcome: ClearCheckedRowOutcome;
}

export interface ClearCheckedCausalSummary {
  requestedOperationId: string | null;
  rows: ClearCheckedCausalRow[];
}

export type ClearCheckedPolicyRejectionOutcome =
  | "revoked-new-intent-required"
  | "retained-newer"
  | "idempotent";

export interface ClearCheckedPolicyRejectionSummary {
  requestedOperationId: string;
  reason: "id-too-large" | "logical-too-wide" | "sequence-too-wide" | "clock-ahead";
  serverTimeMs: number;
  rows: Array<Omit<ClearCheckedCausalRow, "outcome"> & {
    outcome: ClearCheckedPolicyRejectionOutcome;
  }>;
}

/** Legacy reset inputs remain available while hooks migrate to causal reset. */
export type LegacyResetGroceryListInput =
  | { clearChecked?: false }
  | { clearChecked: true; checkoffOperationId?: never; epochsByItemId?: never };

/** Exact direct-only causal envelope for POST /grocery-lists/:mealPlanId/reset. */
export type CausalResetGroceryListInput = {
  clearChecked: true;
  checkoffOperationId: string;
  epochsByItemId: Record<string, string>;
};

export type ResetGroceryListInput =
  | LegacyResetGroceryListInput
  | CausalResetGroceryListInput;

/** Exact causal envelope for PUT /grocery-lists/:id/check-item. */
export interface CausalToggleGroceryItemRequest {
  checkoffEpoch: string;
  operationId: string;
}

/**
 * A decoded causal rejection preserves the server payload verbatim. Callers
 * must not infer causal semantics from a bare HTTP status: unrelated 409/428
 * responses share those statuses elsewhere in the product.
 */
export type CheckoffCausalTransportError =
  | {
      kind: "policy-rejected";
      code: "CHECKOFF_OPERATION_POLICY_REJECTED";
      details: CheckoffPolicyRejectionDetails;
      payload: unknown;
    }
  | {
      kind: "reset-policy-rejected";
      code: "CHECKOFF_OPERATION_POLICY_REJECTED";
      details: ClearCheckedPolicyRejectionSummary;
      payload: unknown;
    }
  | {
      kind: "required";
      code: "CHECKOFF_OPERATION_REQUIRED";
      details: CheckoffRequiredDetails;
      payload: unknown;
    }
  | {
      kind: "conflict";
      code: "CHECKOFF_EPOCH_CONFLICT" | "CHECKOFF_OPERATION_CONFLICT";
      details: undefined;
      payload: unknown;
    };

export type CheckoffPolicyRejectionDetails = {
  reason: "id-too-large" | "logical-too-wide" | "sequence-too-wide" | "clock-ahead";
  oldEpoch: string;
  newEpoch: string;
  rejectedOperationId: string;
  policyCeiling: { maxUtf8Bytes: 512; maxDecimalDigits: 128; maxFutureMs: 300000 };
  serverTimeMs: number;
  checked: boolean;
  acceptedEpoch: string;
  acceptedOperationId: null;
};

export type CheckoffRequiredDetails = {
  checked: boolean;
  acceptedEpoch: string;
  acceptedOperationId: string | null;
};

export type ManualAddCausalTarget =
  | {
      kind: "existing";
      itemId: string;
      checkoffEpoch: string;
      operationId: string;
    }
  | { kind: "create"; expectedAbsent: true; operationId: string };

export type CausalQuantityEnvelope = {
  checkoffEpoch: string;
  checkoffOperationId: string;
};

export type LegacySetGroceryItemQuantityRequest = {
  itemId: string;
  quantity: number | null;
  // Present only for a line whose stored `unit` is "" (grocery line
  // control, #1023) -- see resolveUnitOverride in
  // apps/api/src/services/grocery-unit-override.ts. Sending it for a
  // quantified row 400s (UNIT_NOT_EDITABLE); omitting it for an
  // unquantified row's first quantity 400s (UNIT_REQUIRED).
  unit?: string;
};
export type CausalSetGroceryItemQuantityRequest = LegacySetGroceryItemQuantityRequest & CausalQuantityEnvelope;
export type SetGroceryItemQuantityRequest =
  | LegacySetGroceryItemQuantityRequest
  | CausalSetGroceryItemQuantityRequest;

export type DeleteManualGroceryItemRequest =
  | Record<string, never>
  | CausalQuantityEnvelope;

export type AddGroceryItemRequestWithCausality = AddGroceryItemRequest & {
  causalTarget: ManualAddCausalTarget;
};

/** Authoritative causal boundary returned before a manual grocery write. */
export type ManualGroceryItemPreparation = {
  groceryListId: string;
  serverTimeMs: number;
  target:
    | {
        kind: "existing";
        itemId: string;
        checked: boolean;
        checkoffEpoch: string;
        checkoffOperationId: string | null;
      }
    | { kind: "create"; expectedAbsent: true };
};

export type GroceryItemCausalMutationResult = Omit<
  GroceryItemToggleResult,
  "checkoffEpoch" | "checkoffOperationId"
> & {
  checkoffEpoch: string;
  checkoffOperationId: string | null;
  causal: CheckoffCausalResult;
};

export type ResetGroceryListResponse = GroceryList & {
  causal: ClearCheckedCausalSummary;
};

/**
 * What GET /meal-plans and GET /meal-plans/:id actually project for
 * `groceryList` -- the list itself is also a narrower `{ id, items }`
 * select, not the full `GroceryList` (no mealPlanId/userId/timestamps/cost
 * fields). Fetch GET /grocery-lists/:mealPlanId for the full shape.
 */
export interface MealPlanGroceryList {
  id: string;
  // Additive authoritative database-clock sample for this nested snapshot.
  serverTimeMs?: number;
  items: MealPlanGroceryItem[];
}

// PantryLocation ("fridge" | "freezer" | "pantry" | "spice") is imported
// from @batch-and-bowl/shared above and re-exported at the top of this file
// -- mirrors the `PantryLocation` enum in
// packages/database/prisma/schema.prisma (Track B, B1, issue #373).

export interface PantryItem {
  id: string;
  userId: string;
  ingredientId: string;
  quantity: number | null;
  unit: string | null;
  // #388: true when `quantity` was seeded from the ingredient's curated
  // package label (an accepted staple guess) rather than measured by the
  // user. The API clears it on any manual quantity edit and excludes such
  // rows from low-stock alerts -- see apps/api/src/routes/pantry.ts.
  quantityEstimated: boolean;
  purchasedAt: string;
  expiresAt: string | null;
  notes: string | null;
  location: PantryLocation;
  // Null until the item has been marked opened (see Pantry.tsx's Adjust
  // panel). Shifts the start of the freshness window -- see
  // apps/api/src/services/pantry-freshness.ts.
  openedAt: string | null;
  createdAt: string;
  updatedAt: string;
  ingredient: Ingredient;
  // Additive, read-time-only fields the API derives on every response (GET
  // list, POST, POST /bulk, PUT) -- never persisted. See deriveFreshness in
  // apps/api/src/services/pantry-freshness.ts for the exact math and the
  // documented daysLeft/freshnessPct clamping decisions.
  daysLeft: number | null;
  freshnessPct: number;
  // Pantry v2 (Track B, B5, issue #377): true only when expiresAt is
  // STRICTLY past `now` -- extends B1's tiering rather than overloading
  // daysLeft (which clamps at 0). No-auto-deletion: an expired item is
  // still returned by GET /pantry, just flagged.
  expired: boolean;
}

/**
 * Additive on GET /pantry (list) only (issue #377, B5): true when the
 * household's ACTIVE meal plan already uses this ingredient (reusing B2's
 * active-plan map). Drives whether removing the item can auto-record
 * `consumed` silently or needs the 3-option outcome prompt -- see
 * PantryOutcomeDialog.tsx.
 */
export interface PantryListItem extends PantryItem {
  usedInActivePlan: boolean;
}

/**
 * Pantry v2 (Track B, B5, issue #377). Mirrors the `PantryItemEventOutcome`
 * enum in packages/database/prisma/schema.prisma. A closed, three-value
 * set -- see DEFAULT_NO_EVIDENCE_OUTCOME in
 * apps/api/src/services/pantry-outcomes.ts for why there is deliberately no
 * fourth "unknown" value anywhere in the API or UI.
 */
export type PantryEventOutcome = "consumed" | "expired" | "discarded";

/** GET /pantry/stats?period=month. `coverage` mirrors GroceryList's
 * `costCoverage` shape ({ itemsWithCost, totalItems }) -- two counts, never
 * an ad-hoc percentage -- so the UI can tell "no price data" apart from
 * "genuinely zero". */
export interface PantryStatsCoverage {
  itemsWithCost: number;
  totalItems: number;
}

export interface WeeklyTrendPoint {
  weekStart: string;
  savedValue: number;
  wastedCount: number;
}

export interface PantryStats {
  savedValue: number;
  itemsReusedThisWeek: number;
  wastedCount: number;
  // Empty when there's fewer than 3 distinct weeks of PantryItemEvent data
  // -- the "This month" card must NOT render a trend line in that case (see
  // PantryStatsCard.tsx's never-fabricate contract).
  weeklyTrend: WeeklyTrendPoint[];
  coverage: PantryStatsCoverage;
}

export interface AddPantryItemRequest {
  ingredientId: string;
  quantity?: number;
  unit?: string;
  purchasedAt?: string;
  notes?: string;
  location?: PantryLocation;
  openedAt?: string;
}

export interface UpdatePantryItemRequest {
  quantity?: number | null;
  unit?: string | null;
  purchasedAt?: string;
  notes?: string | null;
  location?: PantryLocation;
  openedAt?: string | null;
}

export interface BulkAddPantryRequest {
  items: AddPantryItemRequest[];
}

/** A recipe reference GET /pantry/expiring's use-it suggestion points to. */
export interface SuggestedRecipe {
  id: string;
  proteinChoiceId?: string | null;
  title: string;
  slug: string;
}

/**
 * What GET /pantry/expiring returns -- a PantryItem plus the server-computed
 * `daysLeft` (see apps/api/src/services/pantry-freshness.ts) and an
 * optional `suggestedRecipe`. `suggestedRecipe` is present ONLY when the
 * household's ACTIVE meal plan actually uses this ingredient -- absent
 * (not null) otherwise, so callers must check for the key rather than
 * assume it and fall back to a placeholder.
 */
export interface PantryExpiringItem extends PantryItem {
  daysLeft: number;
  suggestedRecipe?: SuggestedRecipe;
}

interface AddGroceryItemAmount {
  quantity: number;
  unit: string;
  notes?: string;
}

export type AddGroceryItemRequest = AddGroceryItemAmount &
  (
    | { ingredientId: string }
    | {
        name: string;
        category?: IngredientCategory;
        retailerProduct?: {
          retailer: "kroger";
          productId: string;
          searchTerm: string;
        };
      }
  );

export type IngredientCategory =
  | "produce"
  | "dairy"
  | "meat"
  | "seafood"
  | "bakery"
  | "pantry"
  | "frozen"
  | "beverages"
  | "condiments"
  | "spices"
  | "other";

// Request types
export interface CreateMealPlanRequest {
  startDate: string;
  includedMeals: {
    breakfast: boolean;
    lunch: boolean;
    dinner: boolean;
    // A3 re-review F4: createMealPlanSchema (validators) has accepted this
    // key since #371, and GeneratePlanModal has sent it since the A3 fix
    // round -- this type just hadn't caught up. Its absence here only
    // type-checked because GeneratePlanModal builds the request through an
    // intermediate `const payload = {...}` before calling mutateAsync,
    // which skips TypeScript's excess-property check (that check only
    // fires on an object LITERAL at the call site).
    snacks: boolean;
  };
}

/** Request meal switches retain the API's plural `snacks` key. */
export interface CandidateIncludedMeals {
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  snacks: boolean;
}

/** Complete candidate selection map uses the canonical singular MealType. */
export type CandidateSelectedRecipeIds = Record<MealType, string[]>;

/** Stateless initial, refresh, or authoritative Review computation. */
export interface PreviewMealPlanRequest {
  startDate: string;
  includedMeals?: CandidateIncludedMeals;
  replaceExisting?: boolean;
  seed?: number;
  selectedRecipeIds?: CandidateSelectedRecipeIds;
  refresh?: {
    mealType: MealType;
    presentedRecipeIds: string[];
    /** #813 PR 3: narrows the refresh's candidate pool to these protein tag values (OR semantics). */
    proteinFilter?: string[];
  };
}

/** Authoritative, idempotent candidate-plan commit request. */
export interface CommitMealPlanRequest {
  commitId: string;
  startDate: string;
  includedMeals?: CandidateIncludedMeals;
  seed: number;
  mode: CandidateMode;
  expectedPlan: MealPlanPreview["expectedPlan"];
  revisionFingerprint: string;
  selectedRecipeIds: CandidateSelectedRecipeIds;
  acknowledgeOpenSlots: boolean;
  confirmReplace: boolean;
}

export interface CommitMealPlanResponse {
  mealPlan: MealPlan;
  groceryList: GroceryList;
}

export interface SwapMealRequest {
  operationId?: string;
  mealSlotId: string;
  newRecipeId: string;
  source?: "suggestion";
  proteinChoiceId?: string | null;
}

export interface AddMealRequest {
  proteinChoiceId?: string | null;
  date: string;
  mealType: MealType;
  recipeId: string;
  servings?: number;
}

export interface RecipeFilters {
  cuisine?: string;
  difficulty?: "easy" | "medium" | "hard";
  mealType?: MealType;
  tags?: string[];
  /** #813 PR 3: first-class protein filters (OR semantics; combines with tags). */
  protein?: string[];
  maxTotalTime?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface RecipesResponse {
  recipes: Recipe[];
  pagination: PaginationInfo;
}

// User types

// Promoted to @batch-and-bowl/shared so the web app and the native app
// (apps/mobile) share one source of truth. Re-exported here so existing
// `./types` imports keep working unchanged.
export type { UserProfileResponse } from "@batch-and-bowl/shared";
export type { RatingValue };

export interface TagCountsResponse {
  counts: Record<string, number>;
}

export interface UserPreferences {
  dietaryRestrictions: string[];
  allergies: string[];
  // Request-shape consent only -- terms the user explicitly chose to "Keep
  // anyway" despite not resolving against the known allergen/ingredient
  // vocabulary (see PreferenceListInput's warn-and-confirm flow). The
  // server never persists this field; it's consumed once per request to
  // decide whether to 400 with `unmatched_allergies`.
  acknowledgedUnmatchedAllergies: string[];
  householdSize: number;
  // Onboarding v2 (issue #370/#372, spec §6): the household split serving-
  // size math is moving to. Optional -- omitted = leave unchanged server-
  // side -- but the two must always be sent as a pair (server 400s on a
  // lone key), so every real caller in this app always sends both together.
  householdAdults?: number;
  householdKids?: number;
  skillLevel: "beginner" | "intermediate" | "advanced";
  budgetRange: "budget" | "moderate" | "flexible";
  preferredCuisines: string[];
  dislikedIngredients: string[];
  // Onboarding v3 child 1 (issue #826, epic #821): protein favourites,
  // values restricted to PROTEIN_TAG_VALUES. A scorer/candidate-menu bonus
  // only -- never a filter. Avoided proteins are NOT here -- they're
  // written into dislikedIngredients above as a protein value. Optional
  // (issue #831, D6): a caller whose loaded profile predates this field
  // omits the key entirely rather than sending it to a server whose
  // `.strict()` schema doesn't yet recognize it.
  preferredProteins?: string[];
  // Grocery ordering (issue #322): 5-digit US zip for retailer store
  // selection. Optional = leave unchanged; null = clear.
  preferredZip?: string | null;
  // Onboarding v2 (issue #370/#372, spec §6): persisted onboarding-time
  // defaults, distinct from a meal plan's per-call includedMeals.
  mealTypeDefaults?: {
    breakfast: boolean;
    lunch: boolean;
    dinner: boolean;
    snacks: boolean;
  };
  dinnerNights?: 3 | 5 | 7;
  plainOptionEachNight?: boolean;
  // Onboarding v3 child 2 (issue #827, epic #821): weeknight pace dial.
  // Optional = leave unchanged; null = clear back to "today's behaviour"
  // (== balanced).
  weeknightPace?: "quick" | "balanced" | "relaxed" | null;
  // Meal-prep/leftovers spec §4.5: cookDay (0=Sun..6=Sat). Optional = leave
  // unchanged; null = clear.
  cookDay?: number | null;
  leftoversForLunch?: boolean;
  fulfillmentPreference?: "pickup" | "delivery" | "self";
}

/** A member of a household. */
export interface HouseholdMemberInfo {
  userId: string;
  name: string;
  image: string | null;
  joinedAt: string;
}

/** An invite link for a household. */
export interface HouseholdInvite {
  token: string;
  url: string;
  expiresAt: string;
}

/** A household with members and invite state. */
export interface Household {
  id: string;
  name: string;
  members: HouseholdMemberInfo[];
  activeInvite: HouseholdInvite | null;
}

/** The caller's household, or null if solo. */
export interface HouseholdResponse {
  household: Household | null;
}

/** Preview of an invite for the join page. */
export interface InvitePreview {
  inviterName: string;
  householdName: string;
  expiresAt: string;
}
